<template>
  <v-dialog
    v-model="show"
    content-class="ag-dialog"
    scrollable
    :max-width="maxWidth"
    :persistent="persistent"
    :width="width"
    @click:outside="$emit('click:outside')"
    @after-enter="$emit('afterEnter')"
    @after-leave="$emit('afterLeave')"
  >
    <v-card class="ag-dialog__card" :max-height="height">
      <v-card-title v-if="title" class="ag-dialog__card-title">
        <span class="ag-dialog__card-title-text">{{ title }}</span>
        <AGButtonIcon id="dialog_btn_close" icon="mdi-close" size="md" :disabled="disabledCloseButton" @click="close" />
      </v-card-title>

      <div>
        <slot name="header" />
      </div>

      <v-divider v-if="!hideHeaderSeparator" class="ma-0" />

      <v-card-text class="ag-dialog__card-body" :no-padding="noPadding">
        <slot />
      </v-card-text>

      <template v-if="hasActions">
        <v-divider v-if="!hideFooterSeparator" class="ma-0" />

        <slot v-if="$slots.footer" name="footer" />

        <v-card-actions v-else class="ag-dialog__card-footer">
          <AGButton
            v-if="!hideSecondaryButton"
            id="btn_dialog_secondary"
            type="secondary"
            :disabled="disabledSecondaryButton"
            :left-icon="secondaryButtonIcon"
            :loading="loadingSecondaryButton"
            @click="$emit('click:secondary')"
          >
            {{ secondaryButtonText }}
          </AGButton>
          <v-spacer />
          <AGButton
            v-if="!hideTertiaryButton"
            id="btn_dialog_tertiary"
            type="tertiary"
            :disabled="disabledTertiaryButton"
            :left-icon="tertiaryButtonIcon"
            :loading="loadingTertiaryButton"
            @click="$emit('click:tertiary')"
          >
            {{ tertiaryButtonText }}
          </AGButton>
          <AGButton
            v-if="!hidePrimaryButton"
            id="btn_dialog_primary"
            type="primary"
            :disabled="disabledPrimaryButton"
            :left-icon="primaryButtonIcon"
            :loading="loadingPrimaryButton"
            @click="$emit('click:primary')"
          >
            {{ primaryButtonText }}
          </AGButton>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';

export default {
  components: {
    AGButtonIcon,
    AGButton,
  },

  emits: [
    'update:modelValue',
    'close',
    'click:primary',
    'click:secondary',
    'click:tertiary',
    'click:outside',
    'afterLeave',
    'afterEnter',
  ],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
      required: true,
    },

    title: {
      type: String,
      default: '',
    },

    noPadding: {
      type: Boolean,
      default: false,
    },

    width: {
      type: String,
      default: '500px',
    },

    maxWidth: {
      type: String,
      default: '500px',
    },

    height: {
      type: String,
      default: '550px',
    },

    primaryButtonText: {
      type: String,
      default: 'Salvar',
    },

    secondaryButtonText: {
      type: String,
      default: 'Cancelar',
    },

    tertiaryButtonText: {
      type: String,
      default: 'Cancelar',
    },

    hidePrimaryButton: {
      type: Boolean,
      default: false,
    },

    hideSecondaryButton: {
      type: Boolean,
      default: false,
    },

    hideTertiaryButton: {
      type: Boolean,
      default: false,
    },

    primaryButtonIcon: {
      type: String,
      default: '',
    },

    secondaryButtonIcon: {
      type: String,
      default: '',
    },

    tertiaryButtonIcon: {
      type: String,
      default: '',
    },

    persistent: {
      type: Boolean,
      default: false,
    },

    disabledPrimaryButton: {
      type: Boolean,
      default: false,
    },

    loadingPrimaryButton: {
      type: Boolean,
      default: false,
    },

    disabledSecondaryButton: {
      type: Boolean,
      default: false,
    },

    loadingSecondaryButton: {
      type: Boolean,
      default: false,
    },

    disabledTertiaryButton: {
      type: Boolean,
      default: false,
    },

    loadingTertiaryButton: {
      type: Boolean,
      default: false,
    },

    hideFooterSeparator: {
      type: Boolean,
      default: false,
    },

    hideHeaderSeparator: {
      type: Boolean,
      default: false,
    },

    disabledCloseButton: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    show: {
      get() {
        return this.modelValue;
      },

      set(val) {
        this.$emit('update:modelValue', val);
      },
    },

    hasActions() {
      return !this.hidePrimaryButton || !this.hideSecondaryButton || !this.hideTertiaryButton;
    },
  },

  methods: {
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep(.ag-dialog) {
  .ag-dialog__card {
    padding: 0 !important;
    border: 0 !important;
    display: flex !important;
    flex-direction: column;

    .ag-dialog__card-title {
      padding: 16px;
      display: flex;
      gap: 12px;
      align-items: flex-start;
      justify-content: space-between;

      .ag-dialog__card-title-text {
        flex: 1;
        word-break: break-word;
      }
    }

    .ag-dialog__card-body {
      padding: 16px !important;
      font-size: 16px !important;
      color: $neutral-color-high-medium;

      &[no-padding='true'] {
        padding: 0 !important;
      }
    }

    .ag-dialog__card-footer {
      gap: 16px;
      padding: 16px;

      > .v-btn.v-btn + .v-btn {
        margin: 0 !important;
      }
    }
  }
}
</style>
